// ============================================================================
// COURT SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { courtEndpoints, COURT_BASE_PATH } from '../endpoints/court.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Court,
  CourtListItem,
  CreateCourtRequest,
  UpdateCourtRequest,
  CourtSearchRequest,
} from '../types/court.types';

class CourtService {
  async getAll(params?: CourtSearchRequest): Promise<PaginatedResponse<CourtListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(COURT_BASE_PATH, params)
      : courtEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Court>> {
    const response = await get<ApiResponse<Court>>(courtEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Court>> {
    const response = await get<ApiResponse<Court>>(courtEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateCourtRequest): Promise<ApiResponse<Court>> {
    const response = await post<ApiResponse<Court>>(courtEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateCourtRequest): Promise<ApiResponse<Court>> {
    const response = await put<ApiResponse<Court>>(courtEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(courtEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${courtEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : courtEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const courtService = new CourtService();
export default courtService;
